if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', class QuickViewModal extends ModalDialog {
    constructor() {
      super();
      this.content = this.querySelector('.quick-view-modal__content');
      this.loader = this.querySelector('.quick-view-modal__loader');
      this.titleEl = this.querySelector('.quick-view-modal__title');

      this.cache = new Map();
    }

    show(opener) {
      const productUrl = opener.dataset.productUrl;
      if (!productUrl) return;

      // Reuse the image the card already loaded (its resolved `currentSrc`) so
      // the panel doesn't refetch the same photo.
      const cardImage = opener.closest('.product-card')?.querySelector('.product-card__image-wrapper img');
      this.cardImageSrc = cardImage ? (cardImage.currentSrc || cardImage.src) : null;

      this.titleEl.textContent = opener.dataset.productTitle || '';

      const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}section_id=quick-view`;

      const cached = this.cache.get(url);
      if (cached !== undefined) {
        this.render(cached);
        this.revealSheet(opener);
        return;
      }

      // De-dupe concurrent clicks while the fetch is in flight.
      if (this.pendingUrl === url) return;
      this.pendingUrl = url;

      // On mobile the loading state lives on the tapped button and the sheet
      // stays closed until content is ready — no spinner inside the sheet.
      // Desktop keeps the in-modal loader.
      const onButton = window.matchMedia('(max-width: 749px)').matches;
      if (onButton) {
        this.pendingOpener = opener;
        this.setButtonLoading(opener, true);
      } else {
        super.show(opener);
        this.content.innerHTML = '';
        this.setLoading(true);
      }

      const errorHtml = `<div class="quick-view-modal__error"><a href="${productUrl}">${this.titleEl.textContent || 'View product'}</a></div>`;

      this.fetchWithRetry(url)
        .then((responseText) => {
          const html = this.hoistAssets(responseText);
          this.cache.set(url, html);
          if (onButton) {
            // Render into the hidden sheet, then slide it up populated.
            if (this.pendingOpener === opener) {
              this.render(html);
              this.revealSheet(opener);
            }
          } else if (this.hasAttribute('open') && this.openedBy === opener) {
            this.render(html);
          }
        })
        .catch((error) => {
          console.error('[quick-view]', error);
          if (onButton) {
            if (this.pendingOpener === opener) {
              this.content.innerHTML = errorHtml;
              this.revealSheet(opener);
            }
          } else if (this.hasAttribute('open') && this.openedBy === opener) {
            this.content.innerHTML = errorHtml;
          }
        })
        .finally(() => {
          this.setLoading(false);
          if (onButton) this.setButtonLoading(opener, false);
          if (this.pendingUrl === url) this.pendingUrl = null;
        });
    }

    setButtonLoading(button, isLoading) {
      if (!button) return;
      button.classList.toggle('js-quick-view--loading', isLoading);
      if (isLoading) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    // Force the closed transform to paint before flipping to [open], so Safari
    // runs the mobile slide-up transition instead of collapsing it into a fade.
    revealSheet(opener) {
      const dialog = this.querySelector('.quick-view-modal__dialog');
      if (dialog) void dialog.offsetHeight;
      super.show(opener);
    }

    // One retry to absorb a transient network / dev-server hiccup.
    fetchWithRetry(url, attempt = 0) {
      return fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Quick View request failed: ${response.status}`);
          return response.text();
        })
        .catch((error) => {
          if (attempt >= 1) throw error;
          return new Promise((resolve) => setTimeout(resolve, 500))
            .then(() => this.fetchWithRetry(url, attempt + 1));
        });
    }

    render(html) {
      this.setLoading(false);
      this.content.innerHTML = html;
      this.runInlineScripts();
      this.applyCardImage();
      this.afterRender();
      const dialog = this.querySelector('.quick-view-modal__dialog');
      if (dialog) dialog.scrollTop = 0;
    }

    // Hoist shared <link>/<script src> to <head> once and strip them from the
    // fragment, so cached markup re-injects with no duplicate asset work.
    hoistAssets(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      doc.querySelectorAll('link[rel="stylesheet"], script[src]').forEach((node) => {
        const attr = node.tagName === 'LINK' ? 'href' : 'src';
        const value = node.getAttribute(attr);
        const selector = `${node.tagName.toLowerCase()}[${attr}="${value}"]`;

        if (value && !document.querySelector(selector)) {
          const tag = document.createElement(node.tagName);
          Array.from(node.attributes).forEach((a) => tag.setAttribute(a.name, a.value));
          document.head.appendChild(tag);
        }
        node.remove();
      });

      return doc.body.innerHTML;
    }

    // Inline scripts inserted via innerHTML don't execute; recreate them so they run.
    runInlineScripts() {
      this.content.querySelectorAll('script:not([src])').forEach((oldTag) => {
        const newTag = document.createElement('script');
        Array.from(oldTag.attributes).forEach((a) => newTag.setAttribute(a.name, a.value));
        newTag.appendChild(document.createTextNode(oldTag.innerHTML));
        oldTag.parentNode.replaceChild(newTag, oldTag);
      });
    }

    // Point the panel image at the cached card image; fall back to the
    // section's full-size URL only when there's no card image.
    applyCardImage() {
      const img = this.content.querySelector('[data-quick-view-image]');
      if (!img) return;
      const src = this.cardImageSrc || img.dataset.fallbackSrc;
      if (src) img.src = src;
    }

    // Isolate post-render hooks so one integration error can't wipe the panel.
    afterRender() {
      try {
        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      } catch (e) {
        console.error('[quick-view] PaymentButton', e);
      }

      try {
        if (window.Trustpilot) {
          this.content
            .querySelectorAll('.trustpilot-widget')
            .forEach((widget) => window.Trustpilot.loadFromElement(widget, true));
        }
      } catch (e) {
        console.error('[quick-view] Trustpilot', e);
      }
    }

    hide(preventFocus = false) {
      // Don't wipe content here — it would blank the panel instantly while the
      // box is still fading out. The next show() clears/replaces it.
      this.setLoading(false);
      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    setLoading(isLoading) {
      this.classList.toggle('quick-view-modal--loading', isLoading);
    }
  });
}
